/* eslint-disable @typescript-eslint/no-misused-promises */
import React, { useState } from 'react'
import Box from '@mui/material/Box'
import { FormProvider, useForm, type SubmitHandler, type FieldValues } from 'react-hook-form'
import FormInput from '../../../components/FormElements/FormInput'
import GenericDateTimePicker from '../../../components/GenericDateTimePicker'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { useTheme } from '@mui/material/styles'
import DefaultModalActions from '../../../components/GenericModal/DefaultModalActions'
import ValidationAlert from '../../../components/ValidationAlert'
import { bindSetError, showServerFieldErrors } from '../../../utils/showServerFieldErrors'
import _ from 'lodash'
import { formatDateWithTime } from '../../../helpers/formatDateWithTime'
import ROUTES from '../../../consts/paths'
import { useNavigate } from 'react-router-dom'
import { LastUpdateMeta } from '../../../components/GenericModal/ChangeMetaInfo'
import { useQueryClient, useMutation } from '@tanstack/react-query'

import { TermsManager } from '../../../APIManager'
import { notification } from '../../../components/Notifications'

interface FormType {
  termName: string
  startTimestamp: Date
  endTimestamp: Date
}

interface CreateAndEditTermsProps {
  selectedRow?: Record<string, any> | null
  handleCloseModal?: () => void
  returnTermId?: (termId: string) => void
  returnTermName?: (termName: string) => void
}

export const CreateAndEditTerms: React.FC<CreateAndEditTermsProps> = ({ selectedRow, handleCloseModal = () => { }, returnTermId, returnTermName }: CreateAndEditTermsProps): JSX.Element => {
  const [errors, setErrors] = useState<unknown>({})
  const queryClient = useQueryClient()
  const [loading, setLoading] = useState<boolean>(false)
  const theme = useTheme()
  const isEditAction = selectedRow !== undefined
  const navigate = useNavigate()
  const isEditing = isEditAction

  const { termName, startTimestamp, endTimestamp } = selectedRow?.data ?? {}

  const termFormSchema = yup.object().shape({
    termName: yup.string().required('Term name is required'),
    startTimestamp: yup
      .date()
      // Show this message if the user enters an invalid or unrecognized date format for the start date field.
      // The expected format includes date and time with AM/PM (e.g., MM/DD/YYYY hh:mm AM/PM).
      .typeError('Invalid start date. Please enter a valid date and time in the correct format (e.g., MM/DD/YYYY hh:mm AM/PM).')
      .required('Start Date is required')
      .max(yup.ref('endTimestamp'), 'Start Date must be before End Date'),
    endTimestamp: yup
      .date()
      // Show this message if the user enters an invalid or unrecognized date format for the end date field.
      // The expected format includes date and time with AM/PM (e.g., MM/DD/YYYY hh:mm AM/PM).
      .typeError('Invalid end date. Please enter a valid date and time in the correct format (e.g., MM/DD/YYYY hh:mm AM/PM).')
      .required('End Date is required')
      .min(yup.ref('startTimestamp'), 'End Date must be after Start Date')
  })

  const methods = useForm<FormType>({
    resolver: yupResolver(termFormSchema),
    defaultValues: {
      termName,
      startTimestamp: startTimestamp ?? null,
      endTimestamp: endTimestamp ?? null
    }
  })

  const { mutateAsync: createTermMuate, isPending: isCreatePending } = useMutation({
    meta: { silent: true },
    mutationFn: TermsManager.createTerm,
    onSuccess: (res) => {
      const response = res.data
      notification('Successfully created', { variant: 'success' })
      if (!isEditing && 'termId' in response && !returnTermId) {
        const termId = response.termId
        navigate(`${ROUTES.TERMS_DETAILS}/${termId}`)
      }
      if ('termId' in response) {
        returnTermId?.(response.termId)
      }
      if ('termName' in response) {
        returnTermName?.(response.termName)
      }
    }
  })

  const { mutateAsync: updateTermMuate, isPending: updateTermPending } = useMutation({
    meta: { silent: true },
    mutationFn: TermsManager.updateTerm,
    onSuccess: (res) => {
      const response = res.data
      notification('Successfully updated', { variant: 'success', subTitle: response.termName })
      if ('termId' in response) {
        returnTermId?.(response.termId)
      }
      if ('termName' in response) {
        returnTermName?.(response.termName)
      }
      void queryClient.invalidateQueries({ queryKey: ['getTermById', selectedRow?.data.termId] })
    }
  })

  const { handleSubmit, control, formState: { isDirty, dirtyFields } } = methods

  const onSubmit: SubmitHandler<FormType> = async (data) => {
    const { termName, startTimestamp, endTimestamp } = data
    setLoading(true)
    try {
      const payload = {
        termName,
        startTimestamp: formatDateWithTime(startTimestamp),
        endTimestamp: formatDateWithTime(endTimestamp)
      }
      const changedFields = _.pickBy(data, (_, key) => (dirtyFields as FieldValues)[key])
      const valuesParsed: { termName?: string, startTimestamp?: string, endTimestamp?: string } = {}

      if (changedFields?.startTimestamp) {
        valuesParsed.startTimestamp = formatDateWithTime(changedFields.startTimestamp)
      }
      if (changedFields?.endTimestamp) {
        valuesParsed.endTimestamp = formatDateWithTime(changedFields.endTimestamp)
      }
      await queryClient.invalidateQueries({ queryKey: ['getTermById', selectedRow?.data.termId] })
      isEditing ? await updateTermMuate({ termId: selectedRow?.data?.termId, payload: { ...changedFields, ...valuesParsed } }) : await createTermMuate(payload)
      handleCloseModal()
    } catch (error) {
      setErrors(showServerFieldErrors(error, bindSetError(methods.setError), methods.clearErrors))
    } finally {
      setLoading(false)
    }
  }

  return (
    <FormProvider {...methods}>
      <ValidationAlert errors={errors} />
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box display='flex' flexDirection='column' gap={theme.spacing(6)}>
          <FormInput
            name="termName"
            label="Title"
            placeholder='Title'
            control={control}
            required
          />

          <GenericDateTimePicker
            name="startTimestamp"
            label="Start Date"
            required
            allowPastDates
          />
          <GenericDateTimePicker
            name="endTimestamp"
            label="End Date"
            required
            allowPastDates
          />
        </Box>

        <DefaultModalActions
          destructiveButtonProps={{
            onClick: handleCloseModal,
            disabled: isCreatePending || updateTermPending
          }}
          confirmButtonProps={{
            text: isEditAction ? 'Save' : 'Create',
            loading: loading || isCreatePending || updateTermPending,
            type: 'submit',
            disabled: isEditAction && !isDirty
          }}
          lastUpdateMeta={isEditAction && (
            <LastUpdateMeta lastEditedBy={selectedRow?.data?.modifiedByUsername} lastEditedAt={selectedRow?.data?.modifiedOn} />
          )}

        />
      </form>
    </FormProvider>
  )
}
